/**
 * UI Panel Opener Action Definitions
 *
 * Phase 2 of `gibbs-bmaps-capability-coverage`. Binds the 8 BMaps panel /
 * dialog opener UserActions in `proto/src/cmds/UICmds.js` that the
 * 2026-05-03 audit reported as unreachable from Gibbs.
 *
 * Each ActionDef is a thin no-side-effect wrapper that names a real
 * UserCmd handler. None take typed inputs from prior workflow steps;
 * they're terminal display-class actions the LLM dispatches when the
 * user explicitly asks for a panel.
 *
 * The §11 corpus prompts ("open the sketcher", "open the docking
 * panel", "open the export dialog", "open the fragment manager",
 * "browse available proteins") are already covered by other ActionDefs
 * (`open-sketcher`, `open-docking-dialog`, `open-export-dialog`,
 * `open-fragment-manager`, `open-protein-browser` — all in
 * `actions/ui-operations.js`). This file ADDS coverage for the
 * remaining unreachable openers.
 *
 * @module gibbs/registry/actions/ui-panel-openers
 */

import { param, pre } from '../schema';

// ─── Side-panel control ────────────────────────────────────────────────────

export const openSidePanel = {
    id: 'open-side-panel',
    name: 'Open Side Panel',
    category: 'Workspace Management',
    description: 'Open one of the workspace side panels (left or right). Used to surface a specific page like the compound table, scoring panel, or screening panel into the side bar without forcing the user to click through the menu.',
    inputs: [],
    outputs: [],
    parameters: [
        param('positionId', 'string', 'Which side panel to open', true, { options: ['left', 'right'] }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenSidePanel',
    serverCommand: null,
    jobType: null,
};

export const closeSidePanel = {
    id: 'close-side-panel',
    name: 'Close Side Panel',
    category: 'Workspace Management',
    description: 'Close one of the workspace side panels (left or right).',
    inputs: [],
    outputs: [],
    parameters: [
        param('positionId', 'string', 'Which side panel to close', true, { options: ['left', 'right'] }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'CloseSidePanel',
    serverCommand: null,
    jobType: null,
};

// ─── Compounds / scoring / screening panels ────────────────────────────────

export const openCompoundsPanel = {
    id: 'open-compounds-panel',
    name: 'Open Compounds Panel',
    category: 'Display',
    description: 'Open the compounds panel — the side-bar view of every loaded compound with selection, visibility, and energy info. Use when the user wants to see everything in their workspace at a glance.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenCompoundsPanel',
    serverCommand: null,
    jobType: null,
};

export const openScoringPanel = {
    id: 'open-scoring-panel',
    name: 'Open Scoring Panel',
    category: 'Display',
    description: 'Open the scoring configuration panel. Lets the user inspect and adjust scoring options (GNINA weights, energy filters, etc.) before running a scoring workflow.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenScoringPanel',
    serverCommand: null,
    jobType: null,
};

export const openScreeningPanel = {
    id: 'open-screening-panel',
    name: 'Open Screening Panel',
    category: 'Display',
    description: 'Open the virtual-screening configuration panel. Lets the user pick a library, set screening parameters, and submit a library-screen job. Distinct from the screening run itself (which is `screen my library`).',
    inputs: [],
    outputs: [],
    parameters: [
        param('tabId', 'string', 'Which tab to focus on opening', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenScreeningPanel',
    serverCommand: null,
    jobType: null,
};

// ─── Compound-rename modal ─────────────────────────────────────────────────

export const openRenameCompound = {
    id: 'open-rename-compound',
    name: 'Open Rename Compound',
    category: 'Compound Management',
    description: 'Open the rename-compound modal for a specific compound. Use when the user says "rename compound 3" and wants the BMaps rename UI rather than a chat-driven rename.',
    inputs: [
        param('compound', 'Compound', 'Compound to rename', true),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'OpenRenameCompound',
    serverCommand: null,
    jobType: null,
};

// ─── Import staging entry points ───────────────────────────────────────────

export const stageMoleculeImport = {
    id: 'stage-molecule-import',
    name: 'Stage Molecule Import',
    category: 'Compound Management',
    description: 'Open the molecule import dialog with optional pre-staged content (e.g., a SMILES string or pasted MOL block). The user confirms or edits before the molecule loads. Distinct from `open-import-dialog`, which opens the dialog without staging anything.',
    inputs: [],
    outputs: [],
    parameters: [
        param('stageArgs', 'object', 'Optional pre-staged import payload', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'StageMoleculeImport',
    serverCommand: null,
    jobType: null,
};

export const stageProteinImport = {
    id: 'stage-protein-import',
    name: 'Stage Protein Import',
    category: 'Target Setup',
    description: 'Open the protein import dialog with optional pre-staged content (e.g., a PDB ID or a pasted PDB file). The user confirms before the protein loads. Distinct from `load-protein-from-pdb`, which loads a known PDB ID directly.',
    inputs: [],
    outputs: [],
    parameters: [
        param('stageArgs', 'object', 'Optional pre-staged import payload', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'StageProteinImport',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    openSidePanel,
    closeSidePanel,
    openCompoundsPanel,
    openScoringPanel,
    openScreeningPanel,
    openRenameCompound,
    stageMoleculeImport,
    stageProteinImport,
];
